import CircularProgress from '@material-ui/core/CircularProgress';
import SvgIcon from '@material-ui/core/SvgIcon';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import * as globalActions from '../../actions/global';
import arrowRightIcon from '../../assets/images/arrow-right-solid-white.svg';
import { CHART_TYPES, fetchingState } from '../../constants';
import CustomTooltip from '../_components/customTooltip';
import FormattedNumber from '../_components/formattedNumber';
import RegisterNotificationTooltip from '../_components/registerNotificationTooltip';
import { Cell, Row, StickyTable } from '../_components/stickyTable';
import PIDUnitWrapper from './PIDUnitWrapper';
import PopupBlockerNotification from './popupBlockerNotification';
import selector from './selector';
import UnitWrapper from './UnitWrapper';

import './index.scss';

import * as toastActions from '../../actions/toast';
import asteriskIcon from '../../assets/images/asterisk-solid.svg';
import downIcon from '../../assets/images/caret-down-solid.svg';
import upIcon from '../../assets/images/caret-up-solid.svg';
import chartIcon from '../../assets/images/chart-simple-solid .png';
import tableIcon from '../../assets/images/table-solid.svg';
import { removeNullCharacter } from '../../utils';

const PropertyDetail = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const { apiBaseUrl, token, currentYear, property, office, clientConfig } =
    useSelector(selector, shallowEqual);

  const {
    fetchAdoptedPDF,
    fetchBudgetPDF,
    fetchCurrentYear,
    fetchETRPDF,
    fetchElectionPDF,
    fetchHistoricalRates,
    fetchProperty,
    fetchProposedPDF,
    fetchSearchResultConfig,
    fetchTaxingUnit,
    fetchVoterPDF,
    openHearing,
    pushToast,
    search,
    sendTaxingUnitMessage,
  } = useMemo(
    () =>
      bindActionCreators(
        {
          fetchAdoptedPDF: globalActions.fetchAdoptedPDF,
          fetchBudgetPDF: globalActions.fetchBudgetPDF,
          fetchCurrentYear: globalActions.fetchCurrentYear,
          fetchETRPDF: globalActions.fetchETRPDF,
          fetchElectionPDF: globalActions.fetchElectionPDF,
          fetchHistoricalRates: globalActions.fetchHistoricalRates,
          fetchProperty: globalActions.fetchProperty,
          fetchProposedPDF: globalActions.fetchProposedPDF,
          fetchSearchResultConfig: globalActions.fetchSearchResultConfig,
          fetchTaxingUnit: globalActions.fetchTaxingUnit,
          fetchVoterPDF: globalActions.fetchVoterPDF,
          openHearing: globalActions.openHearing,
          pushToast: toastActions.push,
          search: globalActions.search,
          sendTaxingUnitMessage: globalActions.createTaxingUnitMessage,
        },
        dispatch,
      ),
    [dispatch],
  );

  const popupBlockerShown = localStorage.getItem('popupBlockerShown');
  const [showPopupBlocker, setShowPopupBlocker] = useState(!popupBlockerShown);

  const [isShowLRates, setIsShowLRates] = useState(false);
  const [toogleChartTable, setToggleChartTable] = useState(0);
  const [ownerID, setOwnerID] = useState('');
  const propertyData = property?.getIn(['data', 0])?.toJS();

  const year = currentYear?.get('data');

  const showLastYearTaxes =
    clientConfig.get('data').toJS().showLastYearTaxes || 0;
  const useAccountNumber = Boolean(
    clientConfig.getIn(['data', 'useAccountNumber']),
  );
  const showBudgetInformation = Boolean(
    clientConfig.getIn(['data', 'showBudgetInformation']),
  );
  const customPropertyMessage = clientConfig.getIn(['data', 'propertyMessage']);
  const showCustomPropertyMessage = clientConfig.getIn([
    'data',
    'showCustomPropertyMessage',
  ]);
  const showBetaMessage = clientConfig.getIn(['data', 'showBetaMessage']);
  const betaMessage = clientConfig.getIn(['data', 'betaMessage']);
  const showLastYearTaxRates = Boolean(
    clientConfig.getIn(['data', 'showLastYearTaxRates']),
  );
  const showHistoricalTaxRates = Boolean(
    clientConfig.getIn(['data', 'showHistoricalTaxRates']),
  );
  const showLegislativeExemptionDisclaimer = Boolean(
    clientConfig.getIn(['data', 'showLegislativeExemptionDisclaimer']),
  );
  const legislativeExemptionDisclaimer = clientConfig.getIn([
    'data',
    'legislativeExemptionDisclaimer',
  ]);

  const chartLayoutPosition = clientConfig.getIn([
    'data',
    'chartLayoutPosition',
  ]);
  const chartPosition1 = clientConfig.getIn(['data', 'chartPosition1']);
  const chartPosition2 = clientConfig.getIn(['data', 'chartPosition2']);
  const chartPosition3 = clientConfig.getIn(['data', 'chartPosition3']);
  const showDisclaimerMessageOnRates = clientConfig.getIn([
    'data',
    'showDisclaimerMessageOnRates',
  ]);
  const taxpayerFeedbackEnabled = clientConfig.getIn([
    'data',
    'taxpayerFeedbackEnabled',
  ]);
  const disclaimerMessage = clientConfig.getIn(['data', 'disclaimerMessage']);
  const showNotPublishedForZeroTaxRate = clientConfig.getIn([
    'data',
    'showNotPublishedForZeroTaxRate',
  ]);
  const taxingUnitChartType = clientConfig.getIn([
    'data',
    'taxingUnitChartType',
  ]);
  const showSummarySectionMessage = clientConfig.getIn([
    'data',
    'showSummarySectionMessage',
  ]);
  const summarySectionMessage = clientConfig.getIn([
    'data',
    'summarySectionMessage',
  ]);
  const displaySummarySectionTableFormat = clientConfig.getIn([
    'data',
    'displaySummarySectionTableFormat',
  ]);
  const displaySummarySectionChartFormat = clientConfig.getIn([
    'data',
    'displaySummarySectionChartFormat',
  ]);

  useEffect(() => {
    if (!displaySummarySectionTableFormat) {
      setToggleChartTable(1);
    } else {
      setToggleChartTable(0);
    }
  }, [displaySummarySectionTableFormat]);

  const fetchData = async () => {
    if (params?.ownerId) {
      setOwnerID(params.ownerId);
      fetchProperty({ propertyId: params.propertyId, ownerId: params.ownerId });
    } else {
      const searchOptions = { queryString: params.propertyId };
      fetchSearchResultConfig();
      const response = await search({
        searchOptions,
      });
      if (response?.results && response.results.length === 1) {
        setOwnerID(response.results[0].ownerID);
        fetchProperty({
          propertyId: params.propertyId,
          ownerId: response.results[0].ownerID,
        });
        navigate(
          `/taxTransparency/property/${response.results[0].propID}/${response.results[0].ownerID}`,
        );
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetch once on mount; depending on params would refetch after navigate() redirects to the owner URL
  useEffect(() => {
    fetchData();
    fetchCurrentYear();
  }, []);

  const renderRateValue = (value) => {
    if (
      showNotPublishedForZeroTaxRate &&
      (!value || Number.isNaN(Number(value)) || Number(value) === 0)
    ) {
      return 'Not Available';
    }
    return <FormattedNumber value={value || 0} />;
  };

  const renderRateValueBasedOnRate = (value, rate) => {
    if (
      showNotPublishedForZeroTaxRate &&
      (!rate || Number.isNaN(Number(rate)) || Number(rate) === 0)
    ) {
      return 'Not Available';
    }
    return <FormattedNumber value={value || 0} />;
  };

  const hidePopupBlocker = () => {
    localStorage.setItem('popupBlockerShown', 'shown');
    setShowPopupBlocker(false);
  };

  const submitFeedack = ({
    message,
    isOwner,
    contactPhone,
    contactEmail,
    questions,
    selectedUnitId,
    selectedUnitYear,
    selectedProperty,
  }) => {
    sendTaxingUnitMessage({
      id: selectedUnitId,
      year: selectedUnitYear,
      pID: selectedProperty,
      isOwner,
      message,
      contactPhone,
      contactEmail,
      questions,
      ownerID: propertyData.ownerID,
    });
  };

  const isWaterRate = (unit) => {
    return unit.taxingUnitType?.toLowerCase().includes('water');
  };

  const isUseTaxElectionRate = (unit) => {
    return unit.useMandatoryTaxElectionRate;
  };

  const parseFloatKeep = (value, removeNegative) => {
    if (!value) {
      return 0.0;
    }
    const splitArray = value.toString().split('.');
    const tempValue =
      removeNegative && value.toString()[0] === '-' ? Math.abs(value) : value;
    if (splitArray.length > 1) {
      return parseFloat(tempValue).toFixed(splitArray[1].length);
    }
    return parseFloat(tempValue);
  };

  const buildLineChartConfig = (data, chartType) => {
    let valueA = parseFloat(
      data.tax_effTaxRate && Number(data.tax_effTaxRate)
        ? data.tax_effTaxRate
        : '',
    );
    if (isWaterRate(data) && isUseTaxElectionRate(data)) {
      valueA = parseFloat(
        data.tax_mandatoryTaxElectionRate &&
          Number(data.tax_mandatoryTaxElectionRate)
          ? data.tax_mandatoryTaxElectionRate
          : '',
      );
    }
    const valueB = parseFloat(
      data.tax_rollbackRate && Number(data.tax_rollbackRate)
        ? data.tax_rollbackRate
        : '',
    );
    const plotLines = [];
    if (valueA) {
      plotLines.push({
        value: valueA,
        width: 1,
        color: 'red',
        zIndex: 5,
        label: {
          align: 'center',
          text: `${isWaterRate(data) && isUseTaxElectionRate(data) ? 'Tax Election Rate' : 'No-New Revenue Rate'} ($ ${valueA})`,
          textAlign: 'center',
          y: valueA >= valueB ? -5 : 10,
          style: {
            color: 'black',
          },
        },
      });
    }
    if (valueB) {
      plotLines.push({
        value: valueB,
        width: 1,
        color: 'blue',
        zIndex: 5,
        label: {
          align: 'center',
          text: `Voter-Approval Rate ($ ${valueB})`,
          textAlign: 'center',
          y: valueB > valueA ? -5 : 10,
          style: {
            color: '#black',
          },
        },
      });
    }
    const effVal = parseFloat(
      data.tax_effTaxRate && Number(data.tax_effTaxRate)
        ? data.tax_effTaxRate
        : '',
    );
    const effEle = parseFloat(
      data.tax_mandatoryTaxElectionRate &&
        Number(data.tax_mandatoryTaxElectionRate)
        ? data.tax_mandatoryTaxElectionRate
        : '',
    );
    const effProp = parseFloat(
      data.tax_proposedTaxRate && Number(data.tax_proposedTaxRate)
        ? data.tax_proposedTaxRate
        : '',
    );
    const effRoll = parseFloat(
      data.tax_rollbackRate && Number(data.tax_rollbackRate)
        ? data.tax_rollbackRate
        : '',
    );
    const effAdopt = parseFloat(
      data.tax_adoptedRate && Number(data.tax_adoptedRate)
        ? data.tax_adoptedRate
        : '',
    );
    // const effVal = 1152;
    // const effProp = 1051;
    // const effRoll = 1051;
    // const effAdopt = null;
    const effArray = [];
    if (!isWaterRate(data) && !isUseTaxElectionRate(data) && effVal) {
      effArray.push(effVal);
    }
    if (isWaterRate(data) && isUseTaxElectionRate(data) && effEle) {
      effArray.push(effEle);
    }
    if (effProp) {
      effArray.push(effProp);
    }
    if (effRoll) {
      effArray.push(effRoll);
    }
    if (effAdopt) {
      effArray.push(effAdopt);
    }
    let min = effArray.length > 0 ? Math.min(...effArray) : null;
    let max = effArray.length > 0 ? Math.max(...effArray) : null;

    if (min !== null && max !== null && min !== max && max - min < 20) {
      if (min > 10) {
        max += 10;
        min -= 10;
      } else {
        max += 20;
      }
    } else {
      min = null;
      max = null;
    }
    if (chartType !== CHART_TYPES.COLUMN || taxingUnitChartType !== 'Hybrid') {
      return {
        chart: {
          type: chartType,
          backgroundColor: 'transparent',
          height: 200,
        },
        title: {
          text: '',
        },
        tooltip: {
          valuePrefix: '$',
        },
        xAxis: {
          categories: [
            isWaterRate(data) && isUseTaxElectionRate(data)
              ? 'Tax Election Rate'
              : 'No-New Revenue Rate',
          ].concat(['Proposed Rate', 'Voter-Approval Rate', 'Adopted Rate']),
        },
        yAxis: {
          title: {
            text: '',
          },
          labels: {
            formatter() {
              return `$ ${this.value.toLocaleString('en')}`;
            },
          },
          min: min === null ? undefined : min,
          max: max === null ? undefined : max,
          allowDecimals: false,
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          line: {
            dataLabels: {
              enabled: true,
              formatter() {
                return `$ ${this.y.toLocaleString('en')}`;
              },
            },
          },
        },
        series: [
          {
            name: 'amount',
            data: [
              isWaterRate(data) && isUseTaxElectionRate(data)
                ? parseFloat(
                    data.tax_mandatoryTaxElectionRate &&
                      Number(data.tax_mandatoryTaxElectionRate)
                      ? data.tax_mandatoryTaxElectionRate
                      : '',
                  )
                : parseFloat(
                    data.tax_effTaxRate && Number(data.tax_effTaxRate)
                      ? data.tax_effTaxRate
                      : '',
                  ),
            ].concat([
              parseFloat(
                data.tax_proposedTaxRate && Number(data.tax_proposedTaxRate)
                  ? data.tax_proposedTaxRate
                  : '',
              ),
              parseFloat(
                data.tax_rollbackRate && Number(data.tax_rollbackRate)
                  ? data.tax_rollbackRate
                  : '',
              ),
              parseFloat(
                data.tax_adoptedRate && Number(data.tax_adoptedRate)
                  ? data.tax_adoptedRate
                  : '',
              ),
            ]),
          },
        ],
      };
    }
    return {
      colors: ['#93d4dd', '#F8C938'],
      chart: {
        type: chartType,
        backgroundColor: 'transparent',
        height: 200,
      },
      title: {
        text: '',
      },
      tooltip: {
        valuePrefix: '$',
        pointFormat: '{series.name}: <b>{point.y}</b><br/>',
        pointFormatter() {
          return `Amount: $ ${this.y.toLocaleString('en')}`;
        },
      },
      xAxis: [
        {
          categories: ['Proposed Rate', 'Adopted Rate'],
          labels: {
            style: {
              color: 'black',
            },
          },
        },
      ],
      yAxis: {
        title: {
          text: '',
        },
        labels: {
          formatter() {
            return `$ ${this.value.toLocaleString('en')}`;
          },
        },
        allowDecimals: false,
        plotLines,
        min: min === null ? undefined : min,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: true,
            formatter() {
              return `$ ${this.y}`;
            },
          },
        },
      },
      series: [
        {
          name: 'amount',
          data: [
            parseFloat(
              data.tax_proposedTaxRate && Number(data.tax_proposedTaxRate)
                ? data.tax_proposedTaxRate
                : '',
            ),
            parseFloat(
              data.tax_adoptedRate && Number(data.tax_adoptedRate)
                ? data.tax_adoptedRate
                : '',
            ),
          ],
          colors: ['#93d4dd', '#F8C938'],
          colorByPoint: true,
        },
      ],
    };
  };

  const buildLineHChartConfig = (data, chartType) => {
    const effArray = data.map((d) =>
      parseFloat(d.adoptedRate && Number(d.adoptedRate) ? d.adoptedRate : ''),
    );

    return {
      colors: ['#333eb8'],
      chart: {
        type: chartType,
        backgroundColor: 'transparent',
        height: 200,
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: data.map((d) => d.taxingUnitYr),
      },
      yAxis: {
        title: {
          text: '',
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: true,
          },
        },
      },
      series: [
        {
          name: 'rate',
          data: effArray,
        },
      ],
    };
  };

  const openHearingWindow = async (hearing) => {
    if (!hearing.hearingNotice) {
      return;
    }
    try {
      const response = await openHearing({ id: hearing.taxingUnitHearingID });
      if (response) {
        const blob = new Blob([response], { type: 'application/pdf' });
        if (window.navigator?.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob);
        } else {
          const url = window.URL.createObjectURL(blob);
          window.open(url);
          // // For other browsers:
          // // Create a link pointing to the ObjectURL containing the blob.
          // const data = window.URL.createObjectURL(blob);
          // const link = document.createElement('a');
          // link.href = data;
          // link.target = '_blank';
          // if (/Edge/i.test(navigator.userAgent)) {
          //   link.download = `${hearing.taxingUnitHearingID}.pdf`;
          // }
          // link.click();
          // setTimeout(() => {
          //   // For Firefox it is necessary to delay revoking the ObjectURL
          //   window.URL.revokeObjectURL(data);
          // }, 100);
        }
      } else {
        console.log('Error No Response returned');
      }
    } catch (error) {
      console.log('Error', error);
    }
  };

  const openWorksheetPDF = async (key, id) => {
    let fetchPDF = null;
    if (key === 'budget') {
      fetchPDF = fetchBudgetPDF;
    } else if (key === 'effectiveRate') {
      fetchPDF = fetchETRPDF;
    } else if (key === 'adoptedRate') {
      fetchPDF = fetchAdoptedPDF;
    } else if (key === 'proposedRate') {
      fetchPDF = fetchProposedPDF;
    } else if (key === 'rollbackRate') {
      fetchPDF = fetchVoterPDF;
    } else if (key === 'mandatoryTaxElectionRate') {
      fetchPDF = fetchElectionPDF;
    }
    if (!fetchPDF) {
      return;
    }
    try {
      const response = await fetchPDF({
        id,
        year,
      });
      if (response) {
        const blob = new Blob([response], { type: 'application/pdf' });
        if (window.navigator?.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob);
        } else {
          const url = window.URL.createObjectURL(blob);
          window.open(url);
        }
      }
    } catch (error) {
      console.log('Error', error);
    }
  };

  // Commented out - unused (code that uses it is commented)
  // const onGenerateReport = async reportType => {
  //   setReportLoading(true);
  //   let environment = apiBaseUrl;
  //   if (environment.match(/dev/i)) {
  //     environment = 'dev';
  //   } else if (environment.match(/stag/i)) {
  //     environment = 'stage';
  //   } else if (environment.match(/prod/i)) {
  //     environment = 'prod';
  //   } else {
  //     environment = 'stage';
  //   }
  //   let officeName = null;
  //   if (office !== 'True Prodigy') {
  //     officeName = office.toLowerCase();
  //   }
  //   const database = officeName
  //     ? `${officeName}_appraisal`
  //     : 'appraisal';
  //   const payload = {
  //     jasperSoftParams: {
  //       reportUnitUri: '/public/TrueProdigy/ProdigyAppraisal/Reports/PropertyTaxTransparency',
  //       async: true,
  //       allowInlineScripts: false,
  //       markupType: 'embeddable',
  //       interactive: true,
  //       freshData: false,
  //       saveDataSnapshot: false,
  //       transformerKey: null,
  //       attachmentsPrefix:
  //         `https://${environment === 'dev' ? 'dev' : 'www'}.trueprodigy-reporting.com/jasperserver-pro/rest_v2/reportExecutions/{reportExecutionId}/exports/{exportExecutionId}/attachments/`,
  //       baseUrl: `https://${environment === 'dev' ? 'dev' : 'www'}.trueprodigy-reporting.com/jasperserver-pro`,
  //       outputFormat: 'pdf',
  //       parameters: {
  //         reportParameter: [
  //           {
  //             name: 'TP_DATABASE',
  //             value: [ database ]
  //           },
  //           {
  //             name: 'TP_SELECTED_QUERY',
  //             value: [ `pid = ${propertyData.propID} and pYear = ${year} limit 1` ]
  //           },
  //           {
  //             name: 'TP_SELECTED_PID',
  //             value: [ propertyData.propID ]
  //           },
  //           {
  //             name: 'TP_SELECTED_PYEAR',
  //             value: [ year ]
  //           },
  //           {
  //             name: 'TP_ACCOUNT_ID',
  //             value: [ propertyData.ownerID ]
  //           },
  //         ]
  //       }
  //     }
  //   };
  //   await generateReport({
  //     payload,
  //     fileName: `${propertyData.accountNumber}-Transparency`
  //   });
  //   setReportLoading(false);
  // };

  const convertToFloat = (value) => (value ? parseFloat(value) : 0);

  const buildCircleChartConfig = (data, type, useTaxElectionRate) => {
    let title =
      type === 'adopted'
        ? "<p style='line-height: 16px; text-align: center;'>ADOPTED RATE</p>"
        : "<p style='line-height: 16px; text-align: center;'>NO-NEW REVENUE RATE</p>";
    if (useTaxElectionRate) {
      title = `<p class="test">NO-NEW REVENUE RATE <img src=${asteriskIcon} style='width: 15px; height: 15px'/></p>`;
    }
    const seriesTitle =
      type === 'adopted' ? 'Adopted Rate' : 'No-New Revenue Rate';
    let subTitle = data.totalAdoptedRate
      ? `<p>${data.totalAdoptedRate} / $100</p>`
      : '';
    if (type !== 'adopted') {
      subTitle = data.totalEffTaxRate
        ? `<p>${data.totalEffTaxRate} / $100</p>`
        : '';
    }
    return {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        type: 'pie',
        backgroundColor: '#335168',
      },
      title: {
        text: title,
        useHTML: true,
        style: {
          color: '#ffffff',
          fontWeight: 'bold',
        },
      },
      subtitle: {
        text: subTitle,
        useHTML: true,
        align: 'center',
        y: 40,
        style: {
          color: 'white',
        },
      },
      tooltip: {
        // biome-ignore lint/suspicious/noTemplateCurlyInString: Highcharts pointFormat placeholder
        pointFormat: '${point.y}',
      },
      plotOptions: {
        pie: {
          size: '80%',
          colors: [
            '#bdffd1',
            '#b69fff',
            '#b9fc00',
            '#63cfce',
            '#d22092',
            '#ffc25f',
            '#39bdff',
            '#fff769',
            '#e8e8e8',
            '#f9bde6',
            '#2cb74a',
            '#ff004f',
            '#f98200',
            '#8e8e8e',
            '#a700ff',
          ],
          data: data.taxingUnit.map((u) => [
            u.taxingUnitName,
            convertToFloat(
              type === 'adopted' ? u.tax_adoptedRate : u.tax_effTaxRate,
            ),
          ]),
        },
      },
      series: [
        {
          type: 'pie',
          name: seriesTitle,
          colorByPoint: true,
          dataLabels: {
            verticalAlign: 'top',
            enabled: true,
            color: '#335168',
            connectorWidth: 1,
            distance: -30,
            connectorColor: '#ffffff',
            formatter() {
              return `${Math.round(this.percentage)} %`;
            },
            style: {
              fontSize: 15,
            },
          },
        },
        {
          type: 'pie',
          name: title,
          dataLabels: {
            enabled: true,
            color: '#ffffff',
            connectorWidth: 1,
            distance: 10,
            connectorColor: '#ffffff',
            formatter() {
              return this.point.name;
            },
          },
        },
      ],
    };
  };

  const getTotalTaxes = (key, data) => {
    if (key === 'No-New Revenue Rate') {
      return data.totalEffTaxRate;
    } else if (key === 'Proposed Rate') {
      return data.totalProposedRate;
    } else if (key === 'Voter-Approval Rate') {
      return data.totalRollbackRate;
    }
    return data.totalAdoptedRate;
  };

  const buildBarChartConfig = (data, useTaxElectionRate) => {
    return {
      chart: {
        type: 'column',
        backgroundColor: '#335168',
      },
      title: {
        text: 'TOTAL TAXES',
        style: {
          color: '#ffffff',
          fontWeight: 'bold',
        },
        x: 0,
      },
      xAxis: {
        categories: [
          'No-New Revenue Rate',
          'Proposed Rate',
          'Voter-Approval Rate',
          'Adopted Rate',
        ],
        labels: {
          style: {
            color: '#ffffff',
          },
          useHTML: useTaxElectionRate,
          formatter() {
            if (useTaxElectionRate && this.isFirst) {
              return `<p style='margin-bottom: 0; width: calc(100% - 15px); overflow: hidden; text-overflow: ellipsis;'><span>${
                this.value
              }</span><img src=${asteriskIcon} style='width: 10px; height: 10px; position: absolute; right: 0;'/></p><span style='color: #cfef4a; fill: #cfef4a'>${getTotalTaxes(
                this.value,
                data,
              )} / $100</span>`;
            }
            return `<span>${
              this.value
            }</span><br/><span style='color: #cfef4a; fill: #cfef4a'>${getTotalTaxes(
              this.value,
              data,
            )} / $100</span>`;
          },
        },
        style: {
          color: '#ffffff',
        },
      },
      yAxis: {
        min: 0,
        title: null,
        stackLabels: {
          enabled: true,
          formatter() {
            return `$ ${this.total.toLocaleString('en')}`;
          },
          style: {
            color: '#ffffff',
          },
        },
        labels: {
          style: {
            color: '#ffffff',
          },
        },
        style: {
          color: '#ffffff',
        },
      },
      legend: {
        align: 'right',
        x: -30,
        verticalAlign: 'middle',
        y: 25,
        floating: false,
        layout: 'vertical',
        borderWidth: 0,
        shadow: false,
        width: 150,
        itemStyle: {
          color: '#ffffff',
        },
      },
      tooltip: {
        headerFormat: '<b>{point.x}</b><br/>',
        pointFormatter() {
          return `${this.series.name}: $${this.y.toLocaleString(
            'en',
          )}<br/>Total: $${this.stackTotal.toLocaleString('en')}`;
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: data.taxingUnit.map((u) => ({
        name: u.taxingUnitName,
        data: [
          convertToFloat(u.tax_effTaxRate),
          convertToFloat(u.tax_proposedTaxRate),
          convertToFloat(u.tax_rollbackRate),
          convertToFloat(u.tax_adoptedRate),
        ],
      })),
      colors: [
        '#bdffd1',
        '#b69fff',
        '#b9fc00',
        '#63cfce',
        '#d22092',
        '#ffc25f',
        '#39bdff',
        '#fff769',
        '#e8e8e8',
        '#f9bde6',
        '#2cb74a',
        '#ff004f',
        '#f98200',
        '#8e8e8e',
        '#a700ff',
      ],
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 450,
            },
            chartOptions: {
              legend: {
                align: 'center',
                x: 0,
                verticalAlign: 'bottom',
                y: 0,
                floating: false,
                width: '100%',
              },
            },
          },
        ],
      },
    };
  };

  if (property.get('state') !== fetchingState.LOADED) {
    return (
      <div style={{ textAlign: 'center' }}>
        <CircularProgress style={{ marginTop: 15, display: 'inline-block' }} />
      </div>
    );
  }

  const renderCharts = {
    'Adopted Rate': () => (
      <div className='col-12 col-sm-12 col-md-4'>
        <HighchartsReact
          containerProps={{ 'data-testid': 'summary-chart-adopted-rate' }}
          highcharts={Highcharts}
          immutable
          options={buildCircleChartConfig(propertyData, 'adopted')}
        />
      </div>
    ),
    'Total Taxes': (useTaxElectionRate) => (
      <div className='col-12 col-sm-12 col-md-4 bar-chart-container'>
        <HighchartsReact
          containerProps={{ 'data-testid': 'summary-chart-total-taxes' }}
          highcharts={Highcharts}
          immutable
          options={buildBarChartConfig(propertyData, useTaxElectionRate)}
        />
      </div>
    ),
    'No New Revenue Tax Rate': (useTaxElectionRate) => (
      <div className='col-12 col-sm-12 col-md-4'>
        <HighchartsReact
          containerProps={{
            'data-testid': 'summary-chart-no-new-revenue-rate',
          }}
          highcharts={Highcharts}
          immutable
          options={buildCircleChartConfig(
            propertyData,
            null,
            useTaxElectionRate,
          )}
        />
        <div className='total-tax'>
          <span>
            ${convertToFloat(propertyData.totalTax).toLocaleString('en')}
          </span>{' '}
          PROPERTY TAX
        </div>
      </div>
    ),
  };

  const renderChartsSection = () => {
    const useTaxElectionRate = Boolean(
      propertyData?.taxingUnit &&
        propertyData.taxingUnit.length > 0 &&
        propertyData.taxingUnit.reduce(
          (a, b) => a || isUseTaxElectionRate(b),
          false,
        ),
    );
    return (
      <div className='rates-chart-conatiner'>
        {propertyData.taxingUnit.length <= 20 && (
          <div className='custom-container'>
            <div className='row'>
              <div
                className='col-12 col-sm-12 col-md-12'
                style={{ marginBottom: '30px' }}
              >
                <div className='title'>Summary Total for All Taxing Units</div>
                <span>{year} Property Taxes</span>
              </div>
              {renderCharts[chartPosition1](useTaxElectionRate)}
              {renderCharts[chartPosition2](useTaxElectionRate)}
              {renderCharts[chartPosition3](useTaxElectionRate)}
            </div>
            {useTaxElectionRate && (
              <div className='election-rate-container'>
                <img
                  alt=''
                  aria-hidden='true'
                  src={asteriskIcon}
                  style={{ marginRight: 10, width: 15, height: 15 }}
                />
                The Tax Election Rate is calculated with the No New Revenue
                Rate.
              </div>
            )}
            <div className='disclaimer-container'>
              {showDisclaimerMessageOnRates === 1 && (
                <span>DISCLAIMER: {disclaimerMessage}</span>
              )}
            </div>
          </div>
        )}
        {Boolean(showLastYearTaxRates) && (
          <div
            style={
              !isShowLRates
                ? { paddingBottom: 25, marginTop: 10 }
                : { marginTop: 10 }
            }
          >
            <div className='show-rate-button-wrapper'>
              <button
                aria-expanded={isShowLRates}
                className='link-button'
                style={
                  isShowLRates
                    ? { backgroundColor: '#f8c938', color: '#2b4457' }
                    : { backgroundColor: '#304555', color: '#ffffff' }
                }
                onClick={() => setIsShowLRates(!isShowLRates)}
                type='button'
              >
                View Rate Summary
                <img
                  alt=''
                  aria-hidden='true'
                  className={`arrow-icon ${isShowLRates ? 'arrow-up' : 'arrow-down'}`}
                  src={isShowLRates ? upIcon : downIcon}
                />
              </button>
            </div>
            {isShowLRates && (
              <div className='rate-container total-table'>
                <StickyTable>
                  <Row>
                    <Cell />
                    <Cell>
                      <span className='label'>Last Year's Tax Rate</span>
                    </Cell>
                    <Cell>
                      <span className='label'>No New Revenue</span>
                    </Cell>
                    <Cell>
                      <span className='label'>Proposed</span>
                    </Cell>
                    <Cell>
                      <span className='label'>Difference</span>
                    </Cell>
                    <Cell>
                      <span className='label'>Voter Approval</span>
                    </Cell>
                    <Cell>
                      <span className='label'>Adopted</span>
                    </Cell>
                  </Row>
                  {propertyData.taxingUnit.map((unit) => (
                    <Row key={unit.TaxingUnitID}>
                      <Cell>
                        <span className='label'>{unit.taxingUnitName}</span>
                      </Cell>
                      <Cell>{renderRateValue(unit.prior_adoptedRate)}</Cell>
                      <Cell>
                        {renderRateValue(
                          isWaterRate(unit) && isUseTaxElectionRate(unit)
                            ? unit.mandatoryTaxElectionRate
                            : unit.effTaxRate,
                        )}
                      </Cell>
                      <Cell>{renderRateValue(unit.proposedRate)}</Cell>
                      <Cell>{renderRateValue(unit.difference)}</Cell>
                      <Cell>{renderRateValue(unit.rollbackRate)}</Cell>
                      <Cell>{renderRateValue(unit.adoptedRate)}</Cell>
                    </Row>
                  ))}
                </StickyTable>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Calculate totals function
  const calculateTotals = () => {
    let lastTaxesTotal = 0;
    let revenueRateTaxesTotal = 0;
    let proposedTaxesTotal = 0;

    // Filter out taxing units that also appear in assessments (PIDs should only be in assessments)
    const filteredTaxingUnits =
      propertyData?.taxingUnit?.filter((unit) => {
        const hasMatchingAssessment = propertyData.assessments?.some(
          (assessment) => assessment.taxingUnitName === unit.taxingUnitName,
        );
        return !hasMatchingAssessment;
      }) || [];

    if (filteredTaxingUnits.length > 0) {
      filteredTaxingUnits.forEach((unit) => {
        lastTaxesTotal += Number(unit.lastYearTaxes);
        const revenueTax =
          isWaterRate(unit) && isUseTaxElectionRate(unit)
            ? unit.tax_mandatoryTaxElectionRate
            : unit.tax_effTaxRate;
        revenueRateTaxesTotal += revenueTax ? Number(revenueTax) : 0;
        proposedTaxesTotal += Number(unit.tax_proposedTaxRate);
      });
    }

    if (propertyData?.assessments) {
      propertyData.assessments.forEach((assessment) => {
        proposedTaxesTotal += Number(assessment.annualAssessment || 0);
      });
    }

    return { lastTaxesTotal, revenueRateTaxesTotal, proposedTaxesTotal };
  };

  return (
    <div>
      <div className='intro-container'>
        <div className='logo-container'>
          <span className='main-title detail-header neue-font-medium'>
            {/* <span className='office-title'><strong>{office} Property Taxes</strong></span>
            <br/> */}
            <span className='office-title'>
              Truth in Taxation for {office} County Property Owners
            </span>
          </span>
          {clientConfig.getIn(['data', 'showHB3273Link']) ? (
            <span className='tooltip-info-icon-wrap'>
              <RegisterNotificationTooltip>
                <a
                  className='register-link'
                  href={clientConfig.getIn(['data', 'hb3273Url'])}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  <BellIcon>
                    <g>
                      <path
                        d='M0 12.321a1.026 1.026 0 0 0 1.027 1.027h12.32a1.027 1.027 0 0 0 .767-1.71l-.237-.266a6.169 6.169 0 0 1-1.556-4.094v-.6a5.136 5.136 0 0 0-4.107-5.035v-.616a1.027 1.027 0 1 0-2.053 0v.616a5.136 5.136 0 0 0-4.108 5.031v.6A6.175 6.175 0 0 1 .5 11.371l-.237.266a1.033 1.033 0 0 0-.263.684z'
                        style={{ opacity: 0.4 }}
                      />
                      <path
                        d='M164.107 448a2.053 2.053 0 1 1-4.107 0h4.107z'
                        transform='translate(-154.866 -433.626)'
                      />
                    </g>
                  </BellIcon>
                  <div>Register for Notification</div>
                </a>
              </RegisterNotificationTooltip>
            </span>
          ) : (
            <div />
          )}
        </div>
      </div>
      <div className='page property-detail'>
        {showBetaMessage === 1 && betaMessage && (
          <div className='disclaimer'>{betaMessage}</div>
        )}
        <div className='custom-container property-detail-header'>
          <div className='description'>
            {showCustomPropertyMessage ? (
              <div
                // biome-ignore lint/security/noDangerouslySetInnerHtml: rich-text message authored by CAD admins in client config
                dangerouslySetInnerHTML={{ __html: customPropertyMessage }}
              />
            ) : null}
          </div>
        </div>
        <div className='top-sticky-container'>
          <div className='breadcrumb-container custom-container'>
            <Link to='/'>Property Search</Link>
            <img alt='' className='inline-icon' src={arrowRightIcon} />
            <span>
              {useAccountNumber
                ? `Account #: ${propertyData.accountNumber}`
                : `Prop ID: ${params.propertyId}`}
            </span>
          </div>
          <div className='upper-container custom-container'>
            <div className='summary-container'>
              <div className='row'>
                <div className='col-12 col-sm-6 col'>
                  <div className='summary-cell'>
                    <span className='title'>
                      {useAccountNumber ? 'Account #' : 'Prop ID'}
                    </span>
                    <span>
                      {useAccountNumber
                        ? propertyData.accountNumber
                        : params.propertyId}
                    </span>
                  </div>
                </div>
                <div className='col-12 col-sm-6 col'>
                  <div className='summary-cell'>
                    <span className='title'>Owner </span>
                    <p>{removeNullCharacter(propertyData.ownerName)}</p>
                  </div>
                </div>
                <div className='col-12 col-sm-6 col'>
                  <div className='summary-cell'>
                    <span className='title'>Property Address </span>
                    <p>{removeNullCharacter(propertyData.situs)}</p>
                  </div>
                </div>
                <div className='col-12 col-sm-6 col'>
                  <div className='summary-cell'>
                    <span className='title'>{`Your ${year} Market Value`}</span>
                    <span className='number'>
                      $
                      <FormattedNumber
                        value={parseFloatKeep(propertyData.marketValue)}
                      />
                    </span>
                  </div>
                </div>
                {/* <div className='col-12 col-sm-6 col'>
                  <div className='summary-cell'>
                    <span className='title'>{`Your ${year} Assessed Value`}</span>
                    <span className='number'>
                      $
                      <FormattedNumber value={parseFloatKeep(propertyData.marketValue)} />
                    </span>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {chartLayoutPosition === 'Top' &&
        (displaySummarySectionChartFormat ||
          displaySummarySectionTableFormat) ? (
          <div style={{ position: 'relative' }}>
            <div className='table-chart-toggle-wrap'>
              {displaySummarySectionTableFormat ? (
                <button
                  aria-label='Show table view'
                  aria-pressed={!toogleChartTable}
                  className={`${!toogleChartTable ? 'active-toggle-icon' : 'toggle-icon'}`}
                  type='button'
                  onClick={() => setToggleChartTable(0)}
                >
                  <img alt='' aria-hidden='true' src={tableIcon} />
                </button>
              ) : null}
              {displaySummarySectionChartFormat ? (
                <button
                  aria-label='Show chart view'
                  aria-pressed={Boolean(toogleChartTable)}
                  className={`${toogleChartTable ? 'active-toggle-icon' : 'toggle-icon'}`}
                  type='button'
                  onClick={() => setToggleChartTable(1)}
                >
                  <img alt='' aria-hidden='true' src={chartIcon} />
                </button>
              ) : null}
            </div>
            {toogleChartTable ? (
              renderChartsSection()
            ) : (
              <div className='property-summary-container custom-container'>
                <h3 className='property-summary-header'>
                  A SUMMARY OF YOUR PROPERTY
                </h3>
                <p className='property-summary-sub-header'>
                  {year} PROPERTY TAXES
                </p>
                <div className='propertys-summary-table'>
                  <StickyTable>
                    <Row>
                      <Cell>
                        <p className='label'>Taxing Entity</p>
                      </Cell>
                      {showLastYearTaxes ? (
                        <Cell>
                          <p className='label'>{year - 1}</p>
                          <p className='label'>Property Taxes</p>
                        </Cell>
                      ) : null}
                      <Cell>
                        <p className='label'>{year} No New</p>
                        <div className='label' style={{ marginRight: 15 }}>
                          Revenue Rate Taxes
                          <span className='tooltip-info-icon-wrap'>
                            <CustomTooltip
                              arrow
                              className='tooltip-info-content'
                              content={
                                <div
                                  style={{
                                    maxWidth: '690px',
                                    padding: '6px 20px',
                                    fontSize: '14px',
                                    backgroundColor: '#2f2f2f',
                                  }}
                                >
                                  <div>
                                    {`A calculated rate that would provide the taxing unit with approximately the same amount of revenue
                                        it received in the previous year on properties taxed in both years. This rate calculation does not include
                                        the impact of additional tax revenue resulting from new construction.`}
                                  </div>
                                  <div>
                                    {
                                      '{ie: The districts created under Water Code Section 49 will use current year tax rate}'
                                    }
                                  </div>
                                </div>
                              }
                              placement='top'
                              variant='dark'
                            >
                              <div className='tooltip-info-icon' />
                            </CustomTooltip>
                          </span>
                        </div>
                      </Cell>
                      <Cell>
                        <p className='label'>{year} Proposed</p>
                        <div className='label' style={{ marginRight: 15 }}>
                          Tax Rate Taxes
                          <span className='tooltip-info-icon-wrap'>
                            <CustomTooltip
                              arrow
                              className='tooltip-info-content'
                              content={
                                <div
                                  style={{
                                    maxWidth: '690px',
                                    padding: '6px 20px',
                                    fontSize: '14px',
                                    backgroundColor: '#2f2f2f',
                                  }}
                                >
                                  This is the tax rate that is proposed by the
                                  taxing unit.
                                </div>
                              }
                              placement='top'
                              variant='dark'
                            >
                              <div className='tooltip-info-icon' />
                            </CustomTooltip>
                          </span>
                        </div>
                      </Cell>
                    </Row>
                    {(() => {
                      const totals = calculateTotals();

                      // Filter out taxing units that also appear in assessments (PIDs should only be in assessments)
                      const filteredTaxingUnits =
                        propertyData.taxingUnit?.filter((unit) => {
                          const hasMatchingAssessment =
                            propertyData.assessments?.some(
                              (assessment) =>
                                assessment.taxingUnitName ===
                                unit.taxingUnitName,
                            );
                          return !hasMatchingAssessment;
                        }) || [];

                      return (
                        <>
                          {filteredTaxingUnits.map((unit) => {
                            return (
                              <Row key={unit.TaxingUnitID}>
                                <Cell>
                                  <strong>{unit.taxingUnitName}</strong>
                                </Cell>
                                {showLastYearTaxes ? (
                                  <Cell>
                                    {(() => {
                                      const rendered =
                                        renderRateValueBasedOnRate(
                                          unit.lastYearTaxes,
                                          unit.prior_adoptedRate,
                                        );
                                      if (typeof rendered === 'string') {
                                        return rendered;
                                      }
                                      return <>${rendered}</>;
                                    })()}
                                  </Cell>
                                ) : null}
                                <Cell>
                                  {(() => {
                                    const rendered = renderRateValueBasedOnRate(
                                      isWaterRate(unit) &&
                                        isUseTaxElectionRate(unit)
                                        ? unit.tax_mandatoryTaxElectionRate
                                        : unit.tax_effTaxRate,
                                      unit.effTaxRate,
                                    );
                                    if (typeof rendered === 'string') {
                                      return rendered;
                                    }
                                    return <>${rendered}</>;
                                  })()}
                                </Cell>
                                <Cell>
                                  {(() => {
                                    const rendered = renderRateValueBasedOnRate(
                                      unit.tax_proposedTaxRate,
                                      unit.proposedRate,
                                    );
                                    if (typeof rendered === 'string') {
                                      return rendered;
                                    }
                                    return <>${rendered}</>;
                                  })()}
                                </Cell>
                              </Row>
                            );
                          })}
                          {propertyData.assessments?.map((assessment) => {
                            return (
                              <Row key={`pid-${assessment.recordID}`}>
                                <Cell>
                                  <strong>{assessment.taxingUnitName}</strong>
                                </Cell>
                                {showLastYearTaxes ? (
                                  <Cell>Not Available</Cell>
                                ) : null}
                                <Cell>Not Available</Cell>
                                <Cell>
                                  $
                                  <FormattedNumber
                                    decimalScale={2}
                                    value={assessment.annualAssessment || 0}
                                  />
                                </Cell>
                              </Row>
                            );
                          })}
                          <Row className='total-row'>
                            <Cell>
                              <strong>TOTAL TAX BILL</strong>
                            </Cell>
                            {showLastYearTaxes ? (
                              <Cell>
                                $
                                <FormattedNumber
                                  value={totals.lastTaxesTotal.toFixed(2)}
                                />
                              </Cell>
                            ) : null}
                            <Cell>
                              $
                              <FormattedNumber
                                value={totals.revenueRateTaxesTotal.toFixed(2)}
                              />
                            </Cell>
                            <Cell>
                              $
                              <FormattedNumber
                                value={totals.proposedTaxesTotal.toFixed(2)}
                              />
                            </Cell>
                          </Row>
                        </>
                      );
                    })()}
                  </StickyTable>
                </div>
                {showSummarySectionMessage ? (
                  <p style={{ whiteSpace: 'pre-line' }}>
                    {summarySectionMessage}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        <div className='middle-container'>
          {(() => {
            // Filter out taxing units that also appear in assessments (PIDs should only be in assessments)
            const filteredTaxingUnits =
              propertyData.taxingUnit?.filter((unit) => {
                const hasMatchingAssessment = propertyData.assessments?.some(
                  (assessment) =>
                    assessment.taxingUnitName === unit.taxingUnitName,
                );
                return !hasMatchingAssessment;
              }) || [];

            return (
              <>
                {filteredTaxingUnits.map((unit) => {
                  return (
                    <UnitWrapper
                      apiBaseUrl={apiBaseUrl}
                      buildLineChartConfig={buildLineChartConfig}
                      buildLineHChartConfig={buildLineHChartConfig}
                      fetchHistoricalRates={fetchHistoricalRates}
                      fetchTaxingUnit={fetchTaxingUnit}
                      isUseTaxElectionRate={isUseTaxElectionRate}
                      isWaterRate={isWaterRate}
                      key={unit.taxingUnitID}
                      legislativeExemptionDisclaimer={
                        legislativeExemptionDisclaimer
                      }
                      openHearingWindow={openHearingWindow}
                      openWorksheetPDF={openWorksheetPDF}
                      ownerId={ownerID}
                      params={params}
                      parseFloatKeep={parseFloatKeep}
                      pushToast={pushToast}
                      showBudgetInformation={showBudgetInformation}
                      showHistoricalTaxRates={showHistoricalTaxRates}
                      showLastYearTaxes={showLastYearTaxes}
                      showLegislativeExemptionDisclaimer={
                        showLegislativeExemptionDisclaimer
                      }
                      showNotPublishedForZeroTaxRate={
                        showNotPublishedForZeroTaxRate
                      }
                      submitFeedack={submitFeedack}
                      taxpayerFeedbackEnabled={taxpayerFeedbackEnabled}
                      token={token}
                      unit={unit}
                    />
                  );
                })}
                {propertyData.assessments?.map((assessment, index) => {
                  return (
                    <PIDUnitWrapper
                      apiBaseUrl={apiBaseUrl}
                      assessment={assessment}
                      fetchTaxingUnit={fetchTaxingUnit}
                      key={`pid-${assessment.recordID || index}`}
                      openWorksheetPDF={openWorksheetPDF}
                      params={params}
                      pushToast={pushToast}
                      showBudgetInformation={showBudgetInformation}
                      submitFeedack={submitFeedack}
                      taxpayerFeedbackEnabled={taxpayerFeedbackEnabled}
                      token={token}
                      year={year}
                    />
                  );
                })}
              </>
            );
          })()}
        </div>
        {chartLayoutPosition === 'Bottom' &&
        (displaySummarySectionChartFormat ||
          displaySummarySectionTableFormat) ? (
          <div style={{ position: 'relative' }}>
            <div className='table-chart-toggle-wrap'>
              {displaySummarySectionTableFormat ? (
                <button
                  aria-label='Show table view'
                  aria-pressed={!toogleChartTable}
                  className={`${!toogleChartTable ? 'active-toggle-icon' : 'toggle-icon'}`}
                  type='button'
                  onClick={() => setToggleChartTable(0)}
                >
                  <img alt='' aria-hidden='true' src={tableIcon} />
                </button>
              ) : null}
              {displaySummarySectionChartFormat ? (
                <button
                  aria-label='Show chart view'
                  aria-pressed={Boolean(toogleChartTable)}
                  className={`${toogleChartTable ? 'active-toggle-icon' : 'toggle-icon'}`}
                  type='button'
                  onClick={() => setToggleChartTable(1)}
                >
                  <img alt='' aria-hidden='true' src={chartIcon} />
                </button>
              ) : null}
            </div>
            {toogleChartTable ? (
              renderChartsSection()
            ) : (
              <div className='property-summary-container custom-container'>
                <h3 className='property-summary-header'>
                  A SUMMARY OF YOUR PROPERTY
                </h3>
                <p className='property-summary-sub-header'>
                  {year} PROPERTY TAXES
                </p>
                <div className='propertys-summary-table'>
                  <StickyTable>
                    <Row>
                      <Cell>
                        <p className='label'>Taxing Entity</p>
                      </Cell>
                      <Cell>
                        <p className='label'>{year - 1}</p>
                        <p className='label'>Property Taxes</p>
                      </Cell>
                      <Cell>
                        <p className='label'>{year} No New</p>
                        <p className='label'>Revenue Rate Taxes</p>
                      </Cell>
                      <Cell>
                        <p className='label'>{year} Proposed</p>
                        <p className='label'>Tax Rate Taxes</p>
                      </Cell>
                    </Row>
                    {(() => {
                      const totals = calculateTotals();

                      // Filter out taxing units that also appear in assessments (PIDs should only be in assessments)
                      const filteredTaxingUnits =
                        propertyData.taxingUnit?.filter((unit) => {
                          const hasMatchingAssessment =
                            propertyData.assessments?.some(
                              (assessment) =>
                                assessment.taxingUnitName ===
                                unit.taxingUnitName,
                            );
                          return !hasMatchingAssessment;
                        }) || [];

                      return (
                        <>
                          {filteredTaxingUnits.map((unit) => {
                            return (
                              <Row key={unit.TaxingUnitID}>
                                <Cell>
                                  <strong>{unit.taxingUnitName}</strong>
                                </Cell>
                                <Cell>
                                  {(() => {
                                    const rendered = renderRateValueBasedOnRate(
                                      unit.lastYearTaxes,
                                      unit.prior_adoptedRate,
                                    );
                                    if (typeof rendered === 'string') {
                                      return rendered;
                                    }
                                    return <>${rendered}</>;
                                  })()}
                                </Cell>
                                <Cell>
                                  {(() => {
                                    const rendered = renderRateValueBasedOnRate(
                                      isWaterRate(unit) &&
                                        isUseTaxElectionRate(unit)
                                        ? unit.tax_mandatoryTaxElectionRate
                                        : unit.tax_effTaxRate,
                                      unit.effTaxRate,
                                    );
                                    if (typeof rendered === 'string') {
                                      return rendered;
                                    }
                                    return <>${rendered}</>;
                                  })()}
                                </Cell>
                                <Cell>
                                  {(() => {
                                    const rendered = renderRateValueBasedOnRate(
                                      unit.tax_proposedTaxRate,
                                      unit.proposedRate,
                                    );
                                    if (typeof rendered === 'string') {
                                      return rendered;
                                    }
                                    return <>${rendered}</>;
                                  })()}
                                </Cell>
                              </Row>
                            );
                          })}
                          {propertyData.assessments?.map((assessment) => {
                            return (
                              <Row key={`pid-${assessment.recordID}`}>
                                <Cell>
                                  <strong>{assessment.taxingUnitName}</strong>
                                </Cell>
                                <Cell>Not Available</Cell>
                                <Cell>Not Available</Cell>
                                <Cell>
                                  $
                                  <FormattedNumber
                                    decimalScale={2}
                                    value={assessment.annualAssessment || 0}
                                  />
                                </Cell>
                              </Row>
                            );
                          })}
                          <Row className='total-row'>
                            <Cell>
                              <strong>TOTAL TAX BILL</strong>
                            </Cell>
                            <Cell>
                              $
                              <FormattedNumber
                                value={totals.lastTaxesTotal.toFixed(2)}
                              />
                            </Cell>
                            <Cell>
                              $
                              <FormattedNumber
                                value={totals.revenueRateTaxesTotal.toFixed(2)}
                              />
                            </Cell>
                            <Cell>
                              $
                              <FormattedNumber
                                value={totals.proposedTaxesTotal.toFixed(2)}
                              />
                            </Cell>
                          </Row>
                        </>
                      );
                    })()}
                  </StickyTable>
                </div>
                {showSummarySectionMessage ? (
                  <p style={{ whiteSpace: 'pre-line' }}>
                    {summarySectionMessage}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
        <div className='bottom-container custom-container'>
          <div className='row'>
            <div className='col-12 col-sm-6 col-md-3'>
              <div className='title'>No New Revenue</div>
              <span>
                A calculated rate that would provide the taxing unit with
                approximately the same amount of revenue it received in the
                previous year on properties taxed in both years. This rate
                calculation does not include the impact of additional tax
                revenue resulting from new construction.
                <br />
                (ie: The districts created under Water Code Section 49 will use
                current year tax rate)
              </span>
            </div>
            <div className='col-12 col-sm-6 col-md-3'>
              <div className='title'>Voter Approval</div>
              <span>
                To exceed this tax rate, most taxing jurisdictions must seek
                voter approval. The maximum rate varies based on the type of
                jurisdiction.
              </span>
            </div>
            <div className='col-12 col-sm-6 col-md-3'>
              <div className='title'>Proposed</div>
              <span>
                This is the tax rate that is proposed by the taxing unit.
              </span>
            </div>
            <div className='col-12 col-sm-6 col-md-3'>
              <div className='title'>Adopted</div>
              <span>
                This is the tax rate that was adopted by the taxing unit for tax
                year.
              </span>
            </div>
            {/* <div className='col-12 col-sm-6 col-md-2-5'>
              <div className='title'>Tax Election</div>
              <span>
                Water District election trigger rate. Mandatory or petition election criteria varies based on type of jurisdiction.
              </span>
            </div> */}
          </div>
          <div className='row'>
            <div className='col-12'>
              <span>
                Terms such as “<strong>periodic installment</strong>,” “
                <strong>annual assessment</strong>,” and “
                <strong>total assessment</strong>” are not uniformly defined
                under Texas law and may vary by Public Improvement District
                (PID). Each PID defines these terms in its respective governing
                documents, including its service plan, assessment plan, and
                ordinances, which control how assessments are calculated and
                collected. For property-specific application, consult the
                relevant PID’s official documents or contact the district
                directly.
              </span>
            </div>
          </div>
        </div>
      </div>
      {showPopupBlocker && (
        <PopupBlockerNotification onClose={hidePopupBlocker} />
      )}
    </div>
  );
};

const BellIcon = styled(SvgIcon)`
  margin-bottom: -8px;
`;

export default PropertyDetail;
